import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import { TtsLanguage, UNKNOWN_LANG } from '../../types/tts'
import { tokens } from '../../theme/tokens'

interface LanguagePickerProps {
  languages: TtsLanguage[]
  selectedLanguage: string
  onSelectLanguage: (code: string) => void
  loading?: boolean
}

// How many language chips the first screen shows before the expander. A healthy
// catalog stocks EVERY supported language, so "has voices" alone no longer
// narrows anything — without a cap the picker dumps ~73 chips at once and buries
// the voice list below them. Sized to a few rows of the 380px panel.
const MAX_CURATED_LANGUAGES = 12

const hasVoices = (lang: TtsLanguage): boolean => (lang.voiceCount ?? 0) > 0

/**
 * Language-first selector (catalog v4). Searchable, with a curated subset shown
 * first and a "Show all N languages" expander revealing the full API list.
 *
 * The curated subset is the languages that HAVE voices, capped at
 * MAX_CURATED_LANGUAGES and ranked by how many voices they offer. Voice count is
 * a fact about the catalog, not a capability claim — no language is promoted for
 * its latency tier, because every language streams and ranking en/fr first would
 * imply otherwise. Search always spans the full list, and the selected language
 * is never hidden by the cap.
 *
 * Numbers rendered here: the per-language chip shows that language's VOICE count
 * (labelled as such for screen readers), and the expander shows the count of
 * REAL languages — the synthetic "unrecognized" bucket is not a language and is
 * never counted as one. The two numbers never share a position.
 */
const LanguagePicker: React.FC<LanguagePickerProps> = ({
  languages,
  selectedLanguage,
  onSelectLanguage,
  loading = false,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const [search, setSearch] = useState('')
  const [showAll, setShowAll] = useState(false)

  const q = search.trim().toLowerCase()

  // Real languages only — the synthetic bucket is a holding pen for voices whose
  // language the catalog failed to resolve, never something to count as a language.
  const realLanguages = useMemo(
    () => languages.filter((l) => l.code !== UNKNOWN_LANG),
    [languages]
  )

  const curatedLanguages = useMemo(() => {
    const top = realLanguages
      .filter(hasVoices)
      .sort((a, b) => (b.voiceCount ?? 0) - (a.voiceCount ?? 0) || a.name.localeCompare(b.name))
      .slice(0, MAX_CURATED_LANGUAGES)
    const chosen = new Set(top.map((l) => l.code))

    // The active selection must stay visible even when the cap would drop it —
    // otherwise picking a language via search makes its chip vanish on clear.
    const selected = languages.find((l) => l.code === selectedLanguage && !chosen.has(l.code))
    if (selected) chosen.add(selected.code)

    // The unresolved-language bucket is offered whenever it actually holds
    // voices; the display sort keeps it last.
    const unknown = languages.find(
      (l) => l.code === UNKNOWN_LANG && hasVoices(l) && !chosen.has(l.code)
    )

    return [...top, ...(selected ? [selected] : []), ...(unknown ? [unknown] : [])]
  }, [languages, realLanguages, selectedLanguage])

  // When searching, match across the full list (name / native name / code).
  // Otherwise show the curated subset unless the user expanded the full list.
  const visibleLanguages = useMemo(() => {
    if (q) {
      return languages.filter(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          (l.nativeName && l.nativeName.toLowerCase().includes(q)) ||
          l.code.toLowerCase().includes(q)
      )
    }
    const base = showAll || curatedLanguages.length === 0 ? languages : curatedLanguages
    // Alphabetical by name, except the synthetic "unrecognized" bucket, which is
    // always last so it never leads the list.
    return [...base].sort((a, b) => {
      if ((a.code === UNKNOWN_LANG) !== (b.code === UNKNOWN_LANG)) {
        return a.code === UNKNOWN_LANG ? 1 : -1
      }
      return a.name.localeCompare(b.name)
    })
  }, [q, showAll, languages, curatedLanguages])

  // A count of LANGUAGES — never a voice total, and never inflated by the
  // synthetic bucket, which is not a language.
  const languageCount = realLanguages.length
  const curatedRealCount = curatedLanguages.filter((l) => l.code !== UNKNOWN_LANG).length
  const hiddenCount = languageCount - curatedRealCount
  const canExpand = !q && !showAll && curatedLanguages.length > 0 && hiddenCount > 0

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: isDark ? 'rgba(255,255,255,0.9)' : tokens.colors.tts.main,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontSize: '0.7rem',
          }}
        >
          {t('tts.chooseLanguage', 'Choose a language')}
        </Typography>
      </Box>

      <TextField
        size="small"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('tts.searchLanguages', 'Search languages...')}
        inputProps={{ 'aria-label': t('tts.searchLanguages', 'Search languages...') }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'background.paper',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? tokens.colors.tts.light : tokens.colors.tts.main,
            },
          },
        }}
      />

      {loading && (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
          {t('tts.loadingLanguages', 'Loading languages...')}
        </Typography>
      )}

      {!loading && visibleLanguages.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 1 }}>
          {t('tts.noLanguagesMatch', 'No languages match your search')}
        </Typography>
      )}

      <Box
        role="listbox"
        aria-label={t('tts.chooseLanguage', 'Choose a language')}
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
      >
        {visibleLanguages.map((lang) => {
          const selected = lang.code === selectedLanguage
          const voiceCount = typeof lang.voiceCount === 'number' ? lang.voiceCount : 0
          const voicesLabel = t('tts.voiceCount', {
            defaultValue: '{{count}} voices',
            count: voiceCount,
          })
          return (
            <Chip
              key={lang.code}
              role="option"
              aria-selected={selected}
              tabIndex={0}
              onClick={() => onSelectLanguage(lang.code)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelectLanguage(lang.code)
                }
              }}
              label={
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                  <span>{lang.name}</span>
                  {/* A VOICE count, in a voice context. Titled/labelled so it can
                      never be misread as a count of languages. */}
                  {voiceCount > 0 && (
                    <Box
                      component="span"
                      title={voicesLabel}
                      aria-label={voicesLabel}
                      sx={{
                        fontSize: '0.65rem',
                        opacity: 0.65,
                        fontWeight: 500,
                      }}
                    >
                      {voiceCount}
                    </Box>
                  )}
                </Box>
              }
              variant={selected ? 'filled' : 'outlined'}
              sx={{
                height: 34,
                borderRadius: 2,
                fontWeight: 600,
                px: 0.5,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                ...(selected
                  ? {
                      background: tokens.gradients.tts,
                      color: '#fff',
                      borderColor: 'transparent',
                    }
                  : {
                      backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'background.paper',
                      borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
                      color: isDark ? 'rgba(255,255,255,0.85)' : 'text.primary',
                      '&:hover': {
                        borderColor: isDark ? tokens.colors.tts.light : tokens.colors.tts.main,
                        backgroundColor: isDark
                          ? `${tokens.colors.tts.main}30`
                          : `${tokens.colors.tts.main}0d`,
                      },
                    }),
              }}
            />
          )
        })}
      </Box>

      {canExpand && (
        <Button
          size="small"
          onClick={() => setShowAll(true)}
          endIcon={<ExpandMoreIcon />}
          sx={{
            mt: 1.5,
            textTransform: 'none',
            fontWeight: 600,
            color: isDark ? tokens.colors.tts.light : tokens.colors.tts.main,
          }}
        >
          {t('tts.showAllLanguages', {
            defaultValue: 'Show all {{count}} languages',
            count: languageCount,
          })}
        </Button>
      )}
    </Box>
  )
}

export default LanguagePicker
